/**
 * Parse a 2-column "Expenses (Indirect/Admn.)" sheet: account name | amount.
 * Handles (-)1,234.56 negatives and skips section headers / total rows.
 */

const INDIRECT_GROUP = "Indirect/Admn";

export function parseIndirectExpenseAmount(raw) {
  let s = String(raw ?? "").trim();
  if (!s || s === "-" || s === "—") return NaN;

  let negative = false;
  if (/^\(\s*-\s*\)|^\(-\)/i.test(s)) {
    negative = true;
    s = s.replace(/^\(\s*-\s*\)\s*|^\(-\)\s*/i, "");
  } else if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.replace(/^\(|\)$/g, "");
  } else if (/^-/.test(s)) {
    negative = true;
    s = s.replace(/^-/, "");
  }

  s = s.replace(/,/g, "").replace(/\s+/g, "").trim();
  if (!s || !/[\d.]/.test(s)) return NaN;
  const n = Number(s);
  if (!Number.isFinite(n)) return NaN;
  return negative ? -n : n;
}

function isSectionHeaderLabel(label) {
  const t = String(label || "").trim().toLowerCase();
  if (!t) return false;
  return (
    /expenses?\s*\(/.test(t) ||
    /indirect\s*\/\s*admn/.test(t) ||
    /^expenses?\s*indirect/.test(t) ||
    /^indirect\s*expenses?/.test(t)
  );
}

function isSkippableLabel(label) {
  const t = String(label || "").trim().toLowerCase();
  if (!t) return false;
  return /^total\s*$/.test(t) || /^grand\s*total/.test(t) || /^sub\s*total/.test(t);
}

function rowFromCells(cells) {
  const parts = (Array.isArray(cells) ? cells : []).map((c) => String(c ?? "").trim());
  if (!parts.some((c) => c)) return null;

  let label = "";
  let amountRaw = "";
  let amount = NaN;

  for (let i = 0; i < parts.length; i++) {
    const parsed = parseIndirectExpenseAmount(parts[i]);
    if (Number.isFinite(parsed) && /[\d,().]/.test(parts[i])) {
      amount = parsed;
      amountRaw = parts[i];
    }
  }

  for (let i = 0; i < parts.length; i++) {
    const c = parts[i];
    if (!c || c === amountRaw) continue;
    if (isSectionHeaderLabel(c)) return { skip: true };
    if (isSkippableLabel(c)) return { skip: true };
    label = c;
    break;
  }

  if (!label && Number.isFinite(amount)) {
    return { skip: true, reason: "total" };
  }

  if (!label || !Number.isFinite(amount) || amount === 0) return null;
  if (isSectionHeaderLabel(label) || isSkippableLabel(label)) return { skip: true };

  return {
    particulars: label,
    description: "",
    group: INDIRECT_GROUP,
    qty: "",
    unit: "",
    avgRate: "",
    value: String(amount),
    frQty: "",
    mfr: "",
  };
}

function dropDuplicateTotalRow(rows) {
  if (rows.length < 2) return rows;
  const sum = rows.reduce((s, r) => s + (Number(r.value) || 0), 0);
  const last = Number(rows[rows.length - 1].value) || 0;
  if (Math.abs(last - sum) < 0.02) return rows.slice(0, -1);
  return rows;
}

/**
 * @param {string[][]} aoa - rows from Excel or built from CSV
 */
export function parseIndirectExpenseFromRows(aoa) {
  const rows = [];
  for (const rawRow of aoa || []) {
    const parsed = rowFromCells(rawRow);
    if (!parsed) continue;
    if (parsed.skip) continue;
    rows.push(parsed);
  }
  const cleaned = dropDuplicateTotalRow(rows);
  if (!cleaned.length) {
    throw new Error(
      "No expense lines found. Use two columns: account name and amount (e.g. Bank Charges | 4,102.82)."
    );
  }
  return cleaned;
}

/**
 * @param {string} text - CSV / TSV contents
 */
export function parseIndirectExpenseSheetText(text) {
  const bomStripped = String(text || "").replace(/^\uFEFF/, "");
  const lines = bomStripped
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((l) => l.trimEnd())
    .filter((l) => l.trim().length > 0);

  if (!lines.length) throw new Error("File is empty.");

  const delims = ["\t", ",", ";"];
  let bestDelim = ",";
  let bestScore = -1;
  for (const d of delims) {
    const cols = lines[0].split(d === "\t" ? "\t" : d).length;
    if (cols >= 2 && cols > bestScore) {
      bestScore = cols;
      bestDelim = d;
    }
  }

  const splitLine = (line) => {
    if (bestDelim === "\t") return line.split("\t").map((c) => c.trim());
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        inQ = !inQ;
        continue;
      }
      if (!inQ && c === bestDelim) {
        out.push(cur.trim());
        cur = "";
        continue;
      }
      cur += c;
    }
    out.push(cur.trim());
    return out;
  };

  const aoa = lines.map(splitLine);
  return parseIndirectExpenseFromRows(aoa);
}
